import classNames from 'classnames'
import type { FocusEvent } from 'react'
import { Checkbox } from 'src/modules/core/components/checkbox'
import { Typography } from 'src/modules/core/components/typography'
import type { CheckboxGroupOption, CheckboxGroupProps } from './checkbox-group.interface'
import classes from './checkbox-group.module.scss'

const ACCENT_COLOR = 'var(--bk-control, #5777da)'

export function CheckboxGroup({
  id,
  legend,
  options,
  value,
  onChange,
  onBlur,
  error,
  errorId = `${id}-error`,
  className,
}: CheckboxGroupProps) {
  const handleBlur = (event: FocusEvent<HTMLFieldSetElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onBlur?.()
  }

  const handleToggle = (option: string) => onChange(toggleOption(options, value, option))

  return (
    <fieldset id={id} className={classNames(classes.root, className)} onBlur={handleBlur}>
      <legend className={classes.legend}>
        <Typography
          variant="body"
          element="span"
          className={classNames(classes.legendText, { [classes.legendError]: !!error })}
        >
          {legend}
        </Typography>
      </legend>
      <div className={classes.options}>
        {options.map((option, index) => {
          const optionId = `${id}-option-${index}`
          return (
            <div key={option.value} className={classes.option}>
              <Checkbox
                id={optionId}
                value={option.value}
                checked={value.includes(option.value)}
                onChange={() => handleToggle(option.value)}
                color={ACCENT_COLOR}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
              />
              <label htmlFor={optionId} className={classes.optionLabel}>
                {option.label}
              </label>
            </div>
          )
        })}
      </div>
      {error && (
        <Typography id={errorId} className={classes.error} variant="body">
          {error}
        </Typography>
      )}
    </fieldset>
  )
}

function toggleOption(options: CheckboxGroupOption[], ticked: string[], option: string): string[] {
  const next = ticked.includes(option) ? ticked.filter((value) => value !== option) : [...ticked, option]
  return options.map((candidate) => candidate.value).filter((candidate) => next.includes(candidate))
}
